export const COMBINE_ACCOUNTS_BASE_PATH = '/ads-account/combine';
export const COMBINE_ACCOUNTS_LANDING_PATH = COMBINE_ACCOUNTS_BASE_PATH;
export const COMBINE_ACCOUNTS_CHOOSE_PATH = `${COMBINE_ACCOUNTS_BASE_PATH}/choose`;
export const COMBINE_ACCOUNTS_DETAILS_PATH = `${COMBINE_ACCOUNTS_BASE_PATH}/account-details`;
export const COMBINE_ACCOUNTS_BILLING_PATH = `${COMBINE_ACCOUNTS_BASE_PATH}/billing`;
export const COMBINE_ACCOUNTS_REVIEW_PATH = `${COMBINE_ACCOUNTS_BASE_PATH}/review`;

export const UPGRADE_ACCOUNTS_BASE_PATH = '/ads-account/upgrade';
export const UPGRADE_ACCOUNTS_CHOOSE_PATH = `${UPGRADE_ACCOUNTS_BASE_PATH}/choose`;

const REFERRAL_PARAMS = ['ref_', 'ref'] as const;

/**
 * Step 2 of the manager account creation flow: the account-linking page,
 * hosted by AUMS (their "T3 Link accounts" page). Outside this SPA's router,
 * so reaching it needs a real page navigation rather than a router navigate.
 * (Step 1 is this SPA's /ads-account/manager-account/create route.)
 *
 * The manager account id deliberately fills both the path segment and the
 * entityId query param: on AUMS's manager-account pages the scoping entity is
 * the manager account itself, and their own e2e suite opens this page the
 * same way (AdvertiserUserManagementWebUI features/support/steps).
 *
 * The whole handoff target lives here so that inserting a different step 2
 * later (e.g. a billing page between create and linking) is a change to this
 * one builder.
 *
 * `source=ma-creation` tells the AUMS page the user arrived from creating
 * the account, so it can show a creation success banner; without it the page
 * renders as a plain visit.
 *
 * `ref_` and `ref` identify an A20M referral: advertising.amazon.com/register
 * opens the creation page with `ref_=a20m_us_rgstr_ma&ref=a20m`, and the AUMS
 * page sends such a user to the Products page after linking instead of the
 * Accounts page. `creationPageSearch` is the creation page's own query string
 * (`window.location.search`); only these two names are carried over from it,
 * anything else stays behind.
 *
 * Same contract as {@link ucmAllCampaignsUrl}: the identifier is required and
 * returning undefined rather than an unscoped path is deliberate — the AUMS
 * page is only meaningful for a specific manager account.
 */
export const managerAccountLinkingUrl = (
  managerAccountId?: string,
  creationPageSearch = '',
): string | undefined => {
  if (!managerAccountId) {
    return undefined;
  }
  const maId = encodeURIComponent(managerAccountId);
  const params = new URLSearchParams({ entityId: managerAccountId });
  params.set('source', 'ma-creation');
  const incoming = new URLSearchParams(creationPageSearch);
  for (const name of REFERRAL_PARAMS) {
    const value = incoming.get(name);
    if (value) {
      params.set(name, value);
    }
  }
  return `/am/managerAccounts/${maId}/manage?${params}`;
};

/**
 * Where a user lands after ONE account has been fully upgraded.
 *
 * This is the only case that leaves the combine-and-upgrade area: a single completed upgrade goes
 * to UCM, whereas several completed upgrades, or any failure, stay on the landing page so a banner
 * can report the per-account outcome.
 *
 * Outside this app's route table, so reaching it needs a real page navigation rather than a router
 * navigate, which can only match routes this SPA owns.
 */
export const UCM_ALL_CAMPAIGNS_PATH = '/campaign-manager/all-campaigns';

/** Account settings, where an admin manages details, billing and user permissions. */
export const USER_MANAGEMENT_PATH = '/user/management';

/**
 * Builds the UCM URL, scoped to an entity when one is known.
 *
 * TODO(AUXREQ-3783): the parameter name is assumed to be `entityId` and is unverified against UCM.
 * Every entity-scoped link in this package uses that name, so it is the consistent guess rather than
 * a confirmed one, and a wrong name would land on UCM's default account instead of the new one.
 *
 * The value itself is no longer a gap. The upgrade response carries only the DSP account id, which
 * is not the entity of the account an upgrade produces, but the status endpoint returns
 * `regionalEntityId` alongside `url` once an account reaches CREATED, and both are withheld until
 * then precisely so a link cannot be built into a half-created account.
 */
export const ucmAllCampaignsUrl = (
  entityId?: string,
  accountUrl?: string,
): string | undefined => {
  /*
    Both are required, and returning undefined rather than an unscoped path is
    deliberate: campaign manager would otherwise open whichever account it
    defaults to, which for a user with several is likely the wrong one, right
    after being told an upgrade succeeded. Callers render the link only when this
    returns a value, so a missing identifier hides the action instead of
    misdirecting it.
  */
  if (!entityId || !accountUrl) {
    return undefined;
  }

  const path = `${UCM_ALL_CAMPAIGNS_PATH}?entityId=${encodeURIComponent(
    entityId,
  )}`;

  /*
    An account on another marketplace is only reachable on that marketplace's own
    domain, so its origin is prefixed. Same-marketplace stays relative so it
    remains an in-app navigation rather than a full page load to the same host.
    Mirrors isSellingAccountOnOtherMarketplace in chrome/configuration.
  */
  try {
    const { origin } = new URL(accountUrl);
    return origin === window.location.origin ? path : `${origin}${path}`;
  } catch {
    // An unparseable URL is treated as no URL: better a hidden action than one
    // pointing somewhere unintended.
    return undefined;
  }
};
/**
 * The account settings page for an account, scoped to its entity.
 *
 * Same contract and same reasoning as {@link ucmAllCampaignsUrl}: both identifiers are required, an
 * account on another marketplace is only reachable on that marketplace's own domain so its origin is
 * prefixed, and an unparseable url hides the action rather than pointing somewhere unintended.
 *
 * Split out rather than parameterised by path because the two differ in nothing else today, and a
 * shared builder taking a path would read as though the path were the caller's choice when it is a
 * property of the destination.
 */
export const userManagementUrl = (
  entityId?: string,
  accountUrl?: string,
): string | undefined => {
  if (!entityId || !accountUrl) {
    return undefined;
  }

  const path = `${USER_MANAGEMENT_PATH}?entityId=${encodeURIComponent(
    entityId,
  )}`;

  try {
    const { origin } = new URL(accountUrl);
    return origin === window.location.origin ? path : `${origin}${path}`;
  } catch {
    return undefined;
  }
};
